"use client";

type Entry<T> = { promise: Promise<T>; at: number };

const TTL_MS = 30_000;
const entries = new Map<string, Entry<unknown>>();

function dedupe<T>(key: string, load: () => Promise<T>): Promise<T> {
    const hit = entries.get(key) as Entry<T> | undefined;
    if (hit && Date.now() - hit.at < TTL_MS) return hit.promise;

    const promise = load();
    entries.set(key, { promise, at: Date.now() });
    promise.catch(() => {
        const current = entries.get(key);
        if (current && current.promise === promise) entries.delete(key);
    });
    return promise;
}

export function invalidateSessionCache(key?: string): void {
    if (key) entries.delete(key);
    else entries.clear();
}

export type CurrentUser = Record<string, unknown> | null;

export function fetchCurrentUser(): Promise<CurrentUser> {
    return dedupe("user", async () => {
        const res = await fetch("/api/user");
        if (!res.ok) return null;
        const data = await res.json().catch(() => null);
        return (data?.user ?? null) as CurrentUser;
    });
}

export function fetchMyClasses<
    T = Record<string, unknown>,
>(): Promise<T[] | null> {
    return dedupe("classes/mine", async () => {
        const res = await fetch("/api/classes/mine");
        if (!res.ok) return null;
        const data = await res.json().catch(() => null);
        return Array.isArray(data?.classes) ? (data.classes as T[]) : null;
    });
}
